import { isLoading, LOADING } from "../../task-management/loading";
import { TaskType } from "../../task-management/types/TaskType";

export const getSchemaForTaskPath = (
  taskType: TaskType | typeof LOADING,
  path: string
): any => {
  if (taskType === LOADING) return undefined;

  // Resolves one path part against a schema node, descending into object
  // properties or array items as needed until a direct key match is found.
  const resolvePart = (schema: any, part: string): any => {
    if (schema == null) return undefined;
    if (Array.isArray(schema)) {
      return schema.find((item) => item.name === part)?.schema;
    }
    if (typeof schema === "object" && part in schema) {
      return schema[part];
    }
    if (schema.type === "object" && schema.properties) {
      return resolvePart(schema.properties, part);
    }
    if (schema.type === "array" && schema.items) {
      return resolvePart(schema.items, part);
    }
    return undefined;
  };

  return path.split(".").reduce(resolvePart, taskType as any);
};

type Schema = Record<string, unknown>;

/**
 * Checks if a schema represents a primitive type (string, enum, number, boolean).
 */
function isPrimitiveSchema(schema: Schema): boolean {
  if ("enum" in schema) {
    return true;
  }

  const schemaType = schema["type"];

  if (typeof schemaType === "string") {
    return ["string", "number", "boolean", "integer"].includes(schemaType);
  }

  if (Array.isArray(schemaType)) {
    return schemaType.every(
      (t) =>
        typeof t === "string" &&
        ["string", "number", "boolean", "integer", "null"].includes(t)
    );
  }

  return false;
}

/**
 * Checks if an array schema contains primitive items.
 */
function isArrayOfPrimitivesSchema(schema: Schema): boolean {
  if (schema["type"] !== "array") {
    return false;
  }

  const items = schema["items"];
  if (!items || typeof items !== "object") {
    return false;
  }

  return isPrimitiveSchema(items as Schema);
}

/**
 * Determines if a field should store only the value (not {value, label} objects)
 * based on the task type's schema definition.
 *
 * @param taskType - The task type containing the details schema
 * @param fieldPath - The field path (e.g., "details.strategy", "details.areas")
 * @returns true if the field should store primitive values, false for objects
 */
export function shouldStoreValueOnly(
  taskType: TaskType | typeof LOADING,
  fieldPath: string
): boolean {
  if (isLoading(taskType)) {
    return false;
  }

  const schema = getSchemaForTaskPath(taskType, fieldPath);

  if (!schema) {
    return false;
  }

  if (isPrimitiveSchema(schema)) {
    return true;
  }

  if (isArrayOfPrimitivesSchema(schema)) {
    return true;
  }

  return false;
}
